import React, { useState, useEffect } from "react";
import { TextField, InputAdornment, Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel, IconButton } from "@mui/material";
import { Search as SearchIcon, ExpandMore as ExpandMoreIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import styled from "@emotion/styled";
import axios from "axios";
import { LoaderIcon } from "lucide-react";
import { keyframes } from "@emotion/react";
import VideoPreviewCarousel from "./VideoPreviewCarousel";
import { API } from "../../config/env";

// Add highlight animation
const highlightAnimation = keyframes`
    0% {
        background-color: rgba(255, 75, 75, 0.1);
    }
    100% {
        background-color: transparent;
    }
`;

// Styled components
const TableContainer = styled.div`
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-top: 20px;
    width: 100%;
    max-width: 100%;
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const TableTitle = styled.h2`
    font-family: "Nunito Sans", sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #333;
    margin: 0;
`;

const SearchContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const StyledAccordion = styled(Accordion)`
    margin: 8px 0;
    box-shadow: none;
    border: 1px solid #eee;
    &:before {
        display: none;
    }
    &.Mui-expanded {
        margin: 8px 0;
    }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
    background-color: #F5F7FF;
    min-height: 64px !important;
    .MuiAccordionSummary-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 12px 0;
    }
    &.Mui-expanded {
        min-height: 64px !important;
    }
`;

const VideoTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    width: 100%;
`;

const VideoId = styled.div`
    color: #666;
    font-size: 15px;
    font-weight: 500;
    min-width: 180px;
`;

const VideoName = styled.div`
    display: flex;
    align-items: center;
    font-family: "Nunito Sans", sans-serif;
    font-size: 16px;
    color: #4880FF;
    flex: 1;
`;

const UniversityTag = styled.span`
    background: #E8EEFC;
    color: #4880FF;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    min-width: 120px;
    text-align: center;
`;

const CommentCount = styled.span`
    background: #4880FF;
    color: white;
    min-width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    margin-left: 16px;
`;

const CommentTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
`;

const CommentRow = styled.tr`
    border-bottom: 1px solid #eee;
    &:last-child {
        border-bottom: none;
    }
    &:hover {
        background-color: #F8FAFF;
    }
    &.new-comment {
        animation: ${highlightAnimation} 2s ease-out;
    }
`;

const CommentCell = styled.td`
    padding: 16px;
    font-family: "Nunito Sans", sans-serif;
    font-size: 14px;
    color: #333;
    vertical-align: top;
    line-height: 1.5;
    position: relative;
`;

const CommentHeader = styled.th`
    padding: 16px;
    text-align: left;
    font-family: "Nunito Sans", sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #666;
    background-color: #F8FAFF;
    border-bottom: 1px solid #eee;
`;

const NotificationBadge = styled.span`
    background: #FF4B4B;
    color: white;
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    margin-left: 8px;
    padding: 0 6px;
`;

const NewCommentIndicator = styled.span`
    color: #FF4B4B;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    &:before {
        content: "•";
        margin-right: 4px;
        font-size: 16px;
    }
`;

const ControlsContainer = styled.div`
    display: flex;
    gap: 24px;
    align-items: center;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
    margin: 0;
    .MuiFormControlLabel-label {
        font-family: "Nunito Sans", sans-serif;
        font-size: 14px;
        color: #666;
    }
`;

const FlatCommentRow = styled(CommentRow)`
    background: white;
    &:hover {
        background-color: #F8FAFF;
    }
`;

const VideoNameCell = styled(CommentCell)`
    color: #4880FF;
    font-weight: 500;
`;

const UniversityCell = styled(CommentCell)`
    color: #4880FF;
    span {
        background: #E8EEFC;
        padding: 4px 12px;
        border-radius: 16px;
        font-size: 13px;
    }
`;

const PreviewButton = styled(IconButton)`
    color: #4880FF;
    padding: 4px;
    margin-left: 8px;
    &:hover {
        background-color: rgba(72, 128, 255, 0.1);
    }
`;

const VideoCommentsTable = () => {
    const [loading, setLoading] = useState(true);
    const [videos, setVideos] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedVideo, setExpandedVideo] = useState(null);
    const [isGrouped, setIsGrouped] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
    
    // Add state for tracking new comments (in real app, this would come from your backend)
    const isNewComment = (timestamp) => {
        const twentyFourHoursAgo = new Date();
        twentyFourHoursAgo.setHours(twentyFourHoursAgo.setHours() - 24);
        return new Date(timestamp) > twentyFourHoursAgo;
    };

    useEffect(() => {
        const fetchComments = async () => {
            try {
                setLoading(true);
                const response = await axios.get(
                    `${API.CORE}/api/v1/video-analytics/all/feedback`
                );
                
                // Filter videos with comments and add latest comment timestamp
                const videosWithComments = response.data.data
                    .filter(video => video.comments && video.comments.length > 0)
                    .map(video => ({
                        ...video,
                        latestCommentTimestamp: Math.max(
                            ...video.comments.map(comment => new Date(comment.timestamp).getTime())
                        ),
                        newCommentsCount: video.comments.filter(comment => 
                            isNewComment(comment.timestamp)
                        ).length
                    }))
                    .sort((a, b) => b.latestCommentTimestamp - a.latestCommentTimestamp);
                
                setVideos(videosWithComments);
            } catch (error) {
                console.error("Error fetching comments:", error);
                setVideos([]);
            } finally {
                setLoading(false);
            }
        };

        fetchComments();
    }, []);

    const handleVideoExpand = (videoId) => {
        setExpandedVideo(expandedVideo === videoId ? null : videoId);
    };

    const handleOpenPreview = (video) => {
        const index = videos.findIndex(v => v.video_id === video.video_id);
        setSelectedVideoIndex(index);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
    };

    const filteredVideos = videos.filter(video => {
        const searchString = searchTerm.toLowerCase();
        return (
            video.name?.toLowerCase().includes(searchString) ||
            video.university?.toLowerCase().includes(searchString) ||
            video.comments.some(comment => 
                comment.comment?.toLowerCase().includes(searchString)
            )
        );
    });

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderGroupedView = () => (
        filteredVideos.map(video => (
            <StyledAccordion
                key={video.video_id}
                expanded={expandedVideo === video.video_id}
                onChange={() => handleVideoExpand(video.video_id)}
            >
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <VideoTitle>
                        <VideoId>ID: {video.video_id}</VideoId>
                        <VideoName>
                            {video.name}
                            {video.newCommentsCount > 0 && (
                                <NotificationBadge>{video.newCommentsCount}</NotificationBadge>
                            )}
                        </VideoName>
                        <UniversityTag>{video.university}</UniversityTag>
                        <CommentCount>{video.comments.length}</CommentCount>
                        <PreviewButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenPreview(video);
                            }}
                            title="Preview video"
                        >
                            <VisibilityIcon fontSize="small" />
                        </PreviewButton>
                    </VideoTitle>
                </StyledAccordionSummary>
                <AccordionDetails>
                    <CommentTable>
                        <thead>
                            <tr>
                                <CommentHeader>Comment</CommentHeader>
                                <CommentHeader>Timestamp</CommentHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {video.comments
                                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                                .map((comment, index) => (
                                    <CommentRow key={index}>
                                        <CommentCell>
                                            {comment.comment}
                                            {isNewComment(comment.timestamp) && (
                                                <NewCommentIndicator>New</NewCommentIndicator>
                                            )}
                                        </CommentCell>
                                        <CommentCell>{formatDate(comment.timestamp)}</CommentCell>
                                    </CommentRow>
                                ))}
                        </tbody>
                    </CommentTable>
                </AccordionDetails>
            </StyledAccordion>
        ))
    );

    const renderFlatView = () => {
        const allComments = filteredVideos.flatMap(video => 
            video.comments.map(comment => ({
                ...comment,
                videoId: video.video_id,
                videoName: video.name,
                university: video.university,
                video: video // Pass the entire video object
            }))
        ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        return (
            <CommentTable>
                <thead>
                    <tr>
                        <CommentHeader>Video</CommentHeader>
                        <CommentHeader>University</CommentHeader>
                        <CommentHeader>Comment</CommentHeader>
                        <CommentHeader>Timestamp</CommentHeader>
                        <CommentHeader>Actions</CommentHeader>
                    </tr>
                </thead>
                <tbody>
                    {allComments.map((comment, index) => (
                        <FlatCommentRow key={index}>
                            <VideoNameCell>
                                {comment.videoName}
                                <VideoId>ID: {comment.videoId}</VideoId>
                            </VideoNameCell>
                            <UniversityCell>
                                <span>{comment.university}</span>
                            </UniversityCell>
                            <CommentCell>
                                {comment.comment}
                                {isNewComment(comment.timestamp) && (
                                    <NewCommentIndicator>New</NewCommentIndicator>
                                )}
                            </CommentCell>
                            <CommentCell>{formatDate(comment.timestamp)}</CommentCell>
                            <CommentCell>
                                <PreviewButton
                                    onClick={() => handleOpenPreview(comment.video)}
                                    title="Preview video"
                                >
                                    <VisibilityIcon fontSize="small" />
                                </PreviewButton>
                            </CommentCell>
                        </FlatCommentRow>
                    ))}
                </tbody>
            </CommentTable>
        );
    };

    return (
        <TableContainer>
            <TableHeader>
                <TableTitle>Video Comments</TableTitle>
                <ControlsContainer>
                    <StyledFormControlLabel
                        control={
                            <Switch
                                checked={isGrouped}
                                onChange={(e) => setIsGrouped(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Group by Video"
                    />
                    <TextField
                        variant="outlined"
                        placeholder="Search comments..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: "220px" }}
                    />
                </ControlsContainer>
            </TableHeader>

            {loading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <LoaderIcon className="animate-spin" />
                </div>
            ) : filteredVideos.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                    No comments found.
                </div>
            ) : (
                isGrouped ? renderGroupedView() : renderFlatView()
            )}

            {/* Video Preview Carousel */}
            <VideoPreviewCarousel
                open={previewOpen}
                onClose={handleClosePreview}
                videos={videos}
                initialVideoIndex={selectedVideoIndex}
            />
        </TableContainer>
    );
};

export default VideoCommentsTable; 
